import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASSISTANT_NAME } from '../lib/assistant';
import { VagdeviMark } from '../components/VagdeviMark';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { LESSON_ICON, duration, titleCase } from '../lib/format';
import {
  VideoStage,
  formatClock,
  type VideoManifest,
  type WatchState,
} from '../components/VideoStage';
import type { ApiError } from '../api/client';
import { AskSkillXzenAi } from '../components/AskSkillXzenAi';
import { registerCoursePanel } from '../lib/ai-launcher-bus';
import { QuizStage, type ManifestAssessment } from '../components/QuizStage';
import { RichText } from '../components/RichText';

interface ManifestLesson {
  id: string;
  title: string;
  type: string;
  durationMinutes: number;
  isRequired: boolean;
  isFreePreview: boolean;
  attachmentCount: number;
  locked: boolean;
  lockReason: 'SEQUENTIAL' | 'NO_ENTITLEMENT' | null;
  progress: {
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    percent: number;
    positionSeconds: number;
    completedAt: string | null;
  };
}

interface Manifest {
  /** An author reading the course before learners can; nothing is recorded. */
  preview?: boolean;
  course: {
    id: string;
    title: string;
    slug: string;
    difficulty: string;
    durationMinutes: number;
    navigationMode: 'FREE' | 'SEQUENTIAL';
    category: { name: string };
    instructors: string[];
  };
  access: {
    allowed: boolean;
    source?: string;
    reason?: string;
    message: string;
    trace: Array<{ check: string; outcome: string; detail: string }>;
  };
  enrollment: {
    id: string;
    status: string;
    progress: {
      percent: number;
      lessonTotal: number;
      lessonCompleted: number;
      requiredTotal: number;
      requiredCompleted: number;
      timeSpentSeconds: number;
    };
  } | null;
  modules: Array<{
    id: string;
    title: string;
    description: string | null;
    lessons: ManifestLesson[];
  }>;
  /** Course-level assessments. Optional so an older API still renders. */
  assessments?: ManifestAssessment[];
  resume: { lessonId: string | null };
}

interface LessonContent {
  id: string;
  title: string;
  type: string;
  durationMinutes: number;
  body?: string;
  language?: string | null;
  externalUrl?: string | null;
  streamUrl?: string;
  mimeType?: string | null;
  pageCount?: number | null;
  video?: VideoManifest;
  watch?: WatchState | null;
  attachments: Array<{
    id: string;
    title: string;
    mimeType: string | null;
    downloadUrl: string | null;
  }>;
}

interface Note {
  id: string;
  body: string;
  timestampSeconds: number | null;
  createdAt: string;
}

/** How often an open lesson reports the time spent on it. */
const HEARTBEAT_MS = 30_000;

export function Player() {
  const { courseId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const [manifest, setManifest] = useState<Manifest | null>(null);
  // Full-screen reading: the lesson alone, wide. The browser's own full
  // screen where it exists; a full-window view where it does not (iOS).
  const stageRef = useRef<HTMLElement>(null);
  const [focusMode, setFocusMode] = useState(false);
  useEffect(() => {
    const sync = () =>
      setFocusMode(document.fullscreenElement === stageRef.current && !!stageRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  useEffect(() => {
    if (!focusMode || document.fullscreenElement) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFocusMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusMode]);
  const toggleFocus = () => {
    const stage = stageRef.current;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (focusMode) {
      setFocusMode(false);
    } else if (stage?.requestFullscreen) {
      stage.requestFullscreen().catch(() => setFocusMode(true));
    } else {
      setFocusMode(true);
    }
  };
  // Read inside effects that must not record anything during a preview.
  const previewRef = useRef(false);
  previewRef.current = manifest?.preview ?? false;
  const [content, setContent] = useState<LessonContent | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState('');
  const [panel, setPanel] = useState<'curriculum' | 'notes' | 'ai'>('curriculum');

  // While the player is on screen it owns the assistant: the global launcher
  // asks here instead of opening a second panel for the same course, so a
  // learner never ends up with two threads they cannot see at once.
  useEffect(() => registerCoursePanel(() => setPanel('ai')), []);
  const [watch, setWatch] = useState<WatchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lessonError, setLessonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lessonId = params.get('lesson');
  // An assessment opens in the same stage as a lesson, addressed the same
  // way, so Back and a shared link both land on it.
  const quizId = params.get('quiz');

  const flat = useMemo(
    () => (manifest?.modules ?? []).flatMap((module) => module.lessons),
    [manifest],
  );
  const index = flat.findIndex((lesson) => lesson.id === lessonId);
  const current = index >= 0 ? flat[index] : undefined;

  const assessments = useMemo(() => manifest?.assessments ?? [], [manifest]);
  const quiz = assessments.find((item) => item.id === quizId);
  const pendingRequired = assessments.filter((item) => item.required && !item.passed);
  // The server's own rule for "lessons finished" (ProgressService.recompute):
  // the rollup reaches 100%. The enrolment then reads COMPLETED even with a
  // required assessment outstanding, which is why the player says so below.
  const lessonsDone = (manifest?.enrollment?.progress.percent ?? 0) >= 100;
  const nextQuiz = pendingRequired[0] ?? assessments.find((item) => !item.passed);

  // ------------------------------------------------------------- manifest
  const loadManifest = useCallback(async () => {
    try {
      const next = await api<Manifest>(`/courses/${courseId}/player`);
      setManifest(next);
      return next;
    } catch (caught) {
      setError((caught as ApiError).message);
      return null;
    }
  }, [courseId]);

  useEffect(() => {
    void loadManifest();
  }, [loadManifest]);

  // Land on the resume point rather than an empty stage.
  useEffect(() => {
    if (!manifest || lessonId || quizId) return;
    const target = manifest.resume.lessonId;
    if (target) setParams({ lesson: target }, { replace: true });
  }, [manifest, lessonId, quizId, setParams]);

  // -------------------------------------------------------------- content
  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;

    setContent(null);
    setLessonError(null);
    void (async () => {
      try {
        const [lesson, saved] = await Promise.all([
          api<LessonContent>(`/lessons/${lessonId}/content`),
          api<{ items: Note[] }>(`/lessons/${lessonId}/notes`).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setContent(lesson);
        setWatch(lesson.watch ?? null);
        setNotes(saved.items);
        // Opening a lesson is itself progress; it also moves the resume point.
        // An author's preview records nothing.
        if (!previewRef.current) {
          void api(`/lessons/${lessonId}/progress`, {
            method: 'POST',
            body: JSON.stringify({ percent: 1 }),
          }).catch(() => undefined);
        }
      } catch (caught) {
        if (!cancelled) setLessonError((caught as ApiError).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  // A lesson left open still counts as learning, but only in bounded steps —
  // the server refuses to credit more than fifteen minutes per report.
  const openedAt = useRef(Date.now());
  useEffect(() => {
    openedAt.current = Date.now();
    if (!lessonId) return;
    // A video reports its own watched runs, which already carry the time; a
    // second heartbeat would credit the same minutes twice.
    if (content?.type === 'VIDEO') return;

    const timer = window.setInterval(() => {
      if (document.hidden || previewRef.current) return;
      void api(`/lessons/${lessonId}/progress`, {
        method: 'POST',
        body: JSON.stringify({ timeSpentSeconds: Math.round(HEARTBEAT_MS / 1000) }),
      }).catch(() => undefined);
    }, HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [lessonId, content?.type]);

  // ------------------------------------------------------------- actions
  async function markComplete() {
    if (!lessonId || busy) return;
    setBusy(true);
    try {
      await api(`/lessons/${lessonId}/complete`, { method: 'POST', body: JSON.stringify({}) });
      const next = await loadManifest();
      const upcoming = nextOpenable(next, lessonId);
      if (upcoming) {
        setParams({ lesson: upcoming });
      } else {
        // The last lesson done: if the course still needs a pass, that is
        // where the learner goes next, not a dead end.
        const due = next?.assessments?.find((item) => item.required && !item.passed);
        if (due && (next?.enrollment?.progress.percent ?? 0) >= 100) setParams({ quiz: due.id });
      }
    } catch (caught) {
      setLessonError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function addNote() {
    const body = draft.trim();
    if (!body || !lessonId) return;
    try {
      const note = await api<Note>(`/lessons/${lessonId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setNotes((current) => [...current, note]);
      setDraft('');
    } catch (caught) {
      setLessonError((caught as ApiError).message);
    }
  }

  async function deleteNote(id: string) {
    await api(`/notes/${id}`, { method: 'DELETE' }).catch(() => undefined);
    setNotes((current) => current.filter((note) => note.id !== id));
  }

  // --------------------------------------------------------------- render
  if (error) {
    return (
      <div className="card">
        <div className="state">
          <div className="state__art" aria-hidden="true">
            ⚠
          </div>
          <h4>This course could not be opened</h4>
          <p>{error}</p>
          <Link className="btn btn--sm" to="/my/courses">
            Back to my learning
          </Link>
        </div>
      </div>
    );
  }

  if (!manifest) return <div className="spinner">Loading the course…</div>;

  if (!manifest.access.allowed) {
    return <NoAccess manifest={manifest} />;
  }

  const progress = manifest.enrollment?.progress;

  return (
    <div className="player">
      <header className="player__head">
        <div>
          {manifest.preview ? (
            <Link className="player__back" to={`/admin/courses/${manifest.course.id}`}>
              ← Back to the course builder
            </Link>
          ) : (
            <Link className="player__back" to="/my/courses">
              ← My learning
            </Link>
          )}
          <h1 className="player__title">{manifest.course.title}</h1>
          <p className="player__sub">
            {manifest.course.category.name}
            {manifest.course.instructors[0] ? ` · ${manifest.course.instructors[0]}` : ''}
            {manifest.course.navigationMode === 'SEQUENTIAL' ? ' · Runs in order' : ''}
          </p>
        </div>
        {progress ? (
          <div className="player__progress">
            <div className="player__pct">{progress.percent}%</div>
            <div className="bar" aria-hidden="true">
              <i style={{ width: `${progress.percent}%` }} />
            </div>
            <p className="muted">
              {progress.lessonCompleted} of {progress.lessonTotal} lessons ·{' '}
              {Math.round((progress.timeSpentSeconds / 3600) * 10) / 10}h spent
            </p>
            {/* 100% counts lessons only. Without this line a learner with a
                required quiz outstanding sees a full bar and no certificate,
                and has no way to tell why. */}
            {lessonsDone && pendingRequired.length > 0 ? (
              <p className="player__note player__note--pending" role="status">
                Lessons complete — pass{' '}
                {pendingRequired.length === 1
                  ? `“${pendingRequired[0]!.title}”`
                  : `${pendingRequired.length} required assessments`}{' '}
                to finish.{' '}
                <button
                  type="button"
                  className="player__notelink"
                  onClick={() => setParams({ quiz: pendingRequired[0]!.id })}
                >
                  Take it now
                </button>
              </p>
            ) : lessonsDone && manifest.enrollment?.status === 'COMPLETED' ? (
              <p className="player__note">
                ✓ Course complete · <Link to="/my/certificates">My certificates</Link>
              </p>
            ) : null}
          </div>
        ) : manifest.preview ? (
          <div className="player__preview" role="status">
            <span className="player__preview-badge">Preview</span>
            <p>
              You are seeing this course as learners will. It is not visible to them until it is
              published, and nothing you do here is recorded.
            </p>
          </div>
        ) : (
          <EnrolPrompt courseId={manifest.course.id} onDone={loadManifest} />
        )}
      </header>

      <div className="player__body">
        <aside className="player__rail" aria-label="Course curriculum">
          <div className="player__tabs" role="tablist">
            <button
              role="tab"
              aria-selected={panel === 'curriculum'}
              className={`player__tab${panel === 'curriculum' ? ' player__tab--on' : ''}`}
              onClick={() => setPanel('curriculum')}
            >
              Curriculum
            </button>
            <button
              role="tab"
              aria-selected={panel === 'notes'}
              className={`player__tab${panel === 'notes' ? ' player__tab--on' : ''}`}
              onClick={() => setPanel('notes')}
            >
              Notes{notes.length ? ` (${notes.length})` : ''}
            </button>
            {/* A third tab rather than a new surface: a learner asking about
                the lesson they are on should not have to leave it. */}
            <button
              role="tab"
              aria-selected={panel === 'ai'}
              className={`player__tab${panel === 'ai' ? ' player__tab--on' : ''}`}
              onClick={() => setPanel('ai')}
            >
              <VagdeviMark size={18} /> {ASSISTANT_NAME}
            </button>
          </div>

          {panel === 'curriculum' ? (
            <nav className="rail">
              {manifest.modules.map((module) => (
                <section key={module.id} className="rail__mod">
                  <h2 className="rail__modtitle">{module.title}</h2>
                  {module.description ? (
                    <p className="rail__moddesc">{module.description}</p>
                  ) : null}
                  <ul className="rail__list">
                    {module.lessons.map((lesson) => (
                      <li key={lesson.id}>
                        <button
                          className={`rail__item${lesson.id === lessonId ? ' rail__item--on' : ''}${
                            lesson.locked ? ' rail__item--locked' : ''
                          }`}
                          disabled={lesson.locked}
                          aria-current={lesson.id === lessonId ? 'true' : undefined}
                          onClick={() => setParams({ lesson: lesson.id })}
                        >
                          <span className="rail__state" aria-hidden="true">
                            {lesson.locked
                              ? '🔒'
                              : lesson.progress.status === 'COMPLETED'
                                ? '✓'
                                : (LESSON_ICON[lesson.type] ?? '•')}
                          </span>
                          <span className="rail__name">
                            {lesson.title}
                            {lesson.isFreePreview ? (
                              <span className="preview-pill">Preview</span>
                            ) : null}
                          </span>
                          <span className="rail__dur">{duration(lesson.durationMinutes)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}

              {/* Assessments belong to the course, not to a module (the
                  schema has no module link), so they close the curriculum. */}
              {assessments.length > 0 ? (
                <section className="rail__mod">
                  <h2 className="rail__modtitle">Assessments</h2>
                  <ul className="rail__list">
                    {assessments.map((item) => (
                      <li key={item.id}>
                        <button
                          className={`rail__item${item.id === quizId ? ' rail__item--on' : ''}`}
                          aria-current={item.id === quizId ? 'true' : undefined}
                          onClick={() => setParams({ quiz: item.id })}
                        >
                          <span className="rail__state" aria-hidden="true">
                            {item.passed ? '✓' : '✎'}
                          </span>
                          <span className="rail__name">
                            {item.title}
                            {item.passed ? (
                              <span className="pill pill--active">Passed</span>
                            ) : item.required ? (
                              <span className="pill pill--pending">Required</span>
                            ) : null}
                          </span>
                          <span className="rail__dur">{assessmentNote(item)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </nav>
          ) : null}

          {/* Named explicitly rather than used as the "else" of curriculum.
              It was the else branch, which meant adding a third tab rendered
              the notes panel underneath it. */}
          {panel === 'notes' ? (
            <div className="notes">
              <label className="field">
                <span className="field__label">New note</span>
                <textarea
                  className="field__input"
                  rows={4}
                  value={draft}
                  placeholder="What is worth remembering here?"
                  onChange={(event) => setDraft(event.target.value)}
                />
              </label>
              <button
                className="btn btn--sm"
                onClick={() => void addNote()}
                disabled={!draft.trim()}
              >
                Save note
              </button>

              <ul className="notes__list">
                {notes.map((note) => (
                  <li key={note.id} className="notes__item">
                    <p>{note.body}</p>
                    <button
                      className="iconbtn iconbtn--danger"
                      aria-label="Delete note"
                      onClick={() => void deleteNote(note.id)}
                    >
                      ×
                    </button>
                  </li>
                ))}
                {notes.length === 0 ? (
                  <li className="muted">No notes on this lesson yet.</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {panel === 'ai' ? (
            <AskSkillXzenAi
              courseId={manifest.course.id}
              lessonId={lessonId ?? undefined}
              onOpenSource={(sourceLessonId) => {
                // A citation is only useful if it goes somewhere. Opening the
                // cited lesson is the whole point of showing one.
                setParams({ lesson: sourceLessonId });
                setPanel('curriculum');
              }}
            />
          ) : null}
        </aside>

        <main ref={stageRef} className={`player__stage${focusMode ? ' is-focus' : ''}`}>
          {lessonError && !quizId ? <div className="alert alert--error">{lessonError}</div> : null}

          {quizId ? (
            quiz ? (
              <QuizStage
                key={quiz.id}
                assessment={quiz}
                lessonsDone={lessonsDone}
                otherRequiredPending={pendingRequired.filter((item) => item.id !== quiz.id).length}
                onChanged={() => void loadManifest()}
              />
            ) : (
              <div className="state">
                <div className="state__art" aria-hidden="true">
                  ✎
                </div>
                <h4>This assessment is not available</h4>
                <p>It may have been withdrawn. Pick something from the curriculum.</p>
              </div>
            )
          ) : !lessonId ? (
            <div className="state">
              <div className="state__art" aria-hidden="true">
                ▶
              </div>
              <h4>Pick a lesson to begin</h4>
              <p>Choose anything from the curriculum on the left.</p>
            </div>
          ) : !content ? (
            <div className="skel" style={{ height: 320 }} />
          ) : (
            <>
              <div className="stage__head">
                <div>
                  <p className="stage__eyebrow">
                    {index >= 0 ? (
                      <span className="stage__pos">
                        Lesson {index + 1} of {flat.length}
                      </span>
                    ) : null}
                    {content.type === 'RICH_TEXT'
                      ? `${duration(content.durationMinutes)} read`
                      : `${titleCase(content.type)} · ${duration(content.durationMinutes)}`}
                    {current?.isRequired === false ? ' · Optional' : ''}
                  </p>
                  <h2 className="stage__title">{content.title}</h2>
                </div>
                <div className="stage__tools">
                  <button
                    type="button"
                    className="stage__focus"
                    aria-pressed={focusMode}
                    onClick={toggleFocus}
                    title={focusMode ? 'Exit full screen (Esc)' : 'Read in full screen'}
                  >
                    <span aria-hidden="true">{focusMode ? '✕' : '⛶'}</span>
                    {focusMode ? 'Exit full screen' : 'Full screen'}
                  </button>
                  {manifest.preview ? null : (
                    <CompleteButton
                      completed={current?.progress.status === 'COMPLETED'}
                      busy={busy}
                      watch={content.type === 'VIDEO' ? watch : null}
                      onClick={() => void markComplete()}
                    />
                  )}
                </div>
              </div>

              <LessonStage
                content={content}
                resumeFrom={current?.progress.positionSeconds ?? 0}
                onWatchChange={setWatch}
              />

              {content.attachments.length > 0 ? (
                <section className="stage__files">
                  <h3 className="section__title">Attachments</h3>
                  <ul>
                    {content.attachments.map((file) => (
                      <li key={file.id}>
                        {file.downloadUrl ? (
                          <a href={file.downloadUrl}>{file.title}</a>
                        ) : (
                          <span className="muted">{file.title} (not available)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <nav className="stage__nav">
                <button
                  className="btn btn--outline btn--sm"
                  disabled={index <= 0 || Boolean(flat[index - 1]?.locked)}
                  onClick={() => {
                    const previous = flat[index - 1];
                    if (previous) setParams({ lesson: previous.id });
                  }}
                >
                  ← Previous
                </button>
                {index === flat.length - 1 && nextQuiz ? (
                  // After the last lesson, Next leads on to the assessment
                  // rather than going grey.
                  <button className="btn btn--sm" onClick={() => setParams({ quiz: nextQuiz.id })}>
                    Assessment →
                  </button>
                ) : (
                  <button
                    className="btn btn--sm"
                    disabled={
                      index < 0 || index >= flat.length - 1 || Boolean(flat[index + 1]?.locked)
                    }
                    onClick={() => {
                      const upcoming = flat[index + 1];
                      if (upcoming) setParams({ lesson: upcoming.id });
                    }}
                  >
                    Next →
                  </button>
                )}
              </nav>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/** Renders whichever kind of content the lesson actually is. */
function LessonStage({
  content,
  resumeFrom,
  onWatchChange,
}: {
  content: LessonContent;
  resumeFrom: number;
  onWatchChange: (state: WatchState) => void;
}) {
  // A video carries its own manifest: signed source, captions and chapters.
  if (content.video) {
    return (
      <VideoStage
        lessonId={content.id}
        video={content.video}
        resumeFrom={resumeFrom}
        onWatchChange={onWatchChange}
      />
    );
  }
  if (content.body) {
    // The API sanitises on save, so what arrives here is already safe to render.
    return <RichText html={content.body} />;
  }
  if (content.externalUrl) {
    return (
      <div className="stage__external">
        <p>This lesson is hosted elsewhere.</p>
        <a
          className="btn btn--sm"
          href={content.externalUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Open the lesson ↗
        </a>
      </div>
    );
  }
  if (content.streamUrl) {
    if (content.mimeType?.startsWith('video/')) {
      return <video className="stage__video" src={content.streamUrl} controls playsInline />;
    }
    if (content.mimeType?.startsWith('image/')) {
      return <img className="stage__image" src={content.streamUrl} alt={content.title} />;
    }
    // PDF, and anything else the browser can display inline. How PPT/PPTX is
    // rendered is still open in the BRD (OQ-9), so it downloads for now.
    return (
      <object className="stage__doc" data={content.streamUrl} type={content.mimeType ?? undefined}>
        <p>
          This document cannot be shown inline. <a href={content.streamUrl}>Open it in a new tab</a>
          .
        </p>
      </object>
    );
  }
  return <p className="muted">This lesson has no content attached yet.</p>;
}

function NoAccess({ manifest }: { manifest: Manifest }) {
  const [showTrace, setShowTrace] = useState(false);
  return (
    <div className="card">
      <div className="state">
        <div className="state__art" aria-hidden="true">
          🔒
        </div>
        <h4>{manifest.access.message}</h4>
        <p>
          {manifest.access.reason === 'ENTITLEMENT_EXPIRED'
            ? 'Your progress and history are kept — renewing restores the content.'
            : 'Free preview lessons are still open from the course page.'}
        </p>
        <div className="row">
          <Link className="btn btn--sm" to={`/courses/${manifest.course.slug}`}>
            View the course
          </Link>
          <button className="btn btn--outline btn--sm" onClick={() => setShowTrace((on) => !on)}>
            {showTrace ? 'Hide the detail' : 'Why?'}
          </button>
        </div>

        {showTrace ? (
          <table className="trace">
            <tbody>
              {manifest.access.trace.map((entry, position) => (
                <tr key={`${entry.check}-${position}`}>
                  <td className="mono">{entry.check}</td>
                  <td>
                    <span className={`pill pill--${outcomePill(entry.outcome)}`}>
                      {entry.outcome}
                    </span>
                  </td>
                  <td>{entry.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}

function EnrolPrompt({ courseId, onDone }: { courseId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn btn--sm"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void api('/enrollments', { method: 'POST', body: JSON.stringify({ courseId }) })
          .then(onDone)
          .finally(() => setBusy(false));
      }}
    >
      {busy ? 'Enrolling…' : 'Enrol to track progress'}
    </button>
  );
}

/** The short state on the right of an assessment in the rail. */
function assessmentNote(item: ManifestAssessment): string {
  if (item.passed) return item.recorded?.percent != null ? `${item.recorded.percent}%` : '';
  switch (item.availability) {
    case 'IN_PROGRESS':
      return 'In progress';
    case 'ATTEMPTS_EXHAUSTED':
      return 'No attempts left';
    case 'COOLDOWN':
      return 'Try later';
    case 'NOT_ENROLLED':
      return 'Enrol first';
    default:
      return item.attemptsRemaining === null
        ? `${item.questionCount} Qs`
        : `${item.attemptsRemaining} ${item.attemptsRemaining === 1 ? 'try' : 'tries'} left`;
  }
}

function outcomePill(outcome: string): string {
  if (outcome === 'ALLOW') return 'approved';
  if (outcome === 'DENY') return 'error';
  return 'inactive';
}

/** The next lesson that is not locked and not already finished. */
function nextOpenable(manifest: Manifest | null, after: string): string | null {
  if (!manifest) return null;
  const flat = manifest.modules.flatMap((module) => module.lessons);
  const from = flat.findIndex((lesson) => lesson.id === after);
  return (
    flat.slice(from + 1).find((lesson) => !lesson.locked && lesson.progress.status !== 'COMPLETED')
      ?.id ?? null
  );
}

/**
 * Completion for a video is the server's decision (BRD FR-VID-006), so the
 * button shows how far off the learner is rather than failing on click. It is
 * still only a hint: pressing it anyway is refused by the API.
 */
function CompleteButton({
  completed,
  busy,
  watch,
  onClick,
}: {
  completed: boolean;
  busy: boolean;
  watch: WatchState | null;
  onClick: () => void;
}) {
  if (completed) {
    return (
      <button className="btn btn--outline btn--sm" disabled>
        ✓ Completed
      </button>
    );
  }

  const gated = watch !== null && watch.durationSeconds !== null && !watch.canComplete;

  return (
    <div className="stage__complete">
      <button className="btn btn--sm" onClick={onClick} disabled={busy || gated}>
        Mark complete
      </button>
      {watch && watch.durationSeconds !== null ? (
        <p className="stage__coverage">
          Watched {watch.coveragePercent}% of {formatClock(watch.durationSeconds)}
          {gated ? ` — ${watch.thresholdPercent}% needed` : ''}
        </p>
      ) : null}
    </div>
  );
}
